<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { useFileStore } from '@/stores/file';
import { useGlobalStore } from '@/stores/global';
import { isSupportedFileExtension } from '@/types/fileExtensions';
import { base64toBlob, getRawFileData } from '@/utils/file';
import toast from '@/utils/toast';
import { onMounted, ref } from 'vue';

const fileStore = useFileStore();
const globalStore = useGlobalStore();
const authStore = useAuthStore();

const props = defineProps<{
    src: string;
}>();

const fileBlob = ref<Blob | null>(null);
const fileExtension = ref<string>();
const fileContentType = ref<string>();
const rawFileData = ref<Uint8Array | string | null>(null);
const isInitializing = ref(true);

onMounted(async () => {
    try {
        const base64WithMetaData = await fileStore.getFile(authStore.getCurrentOrganization()._id, props.src);
        const blob = await base64toBlob(base64WithMetaData.base64, base64WithMetaData.contentType);
        fileBlob.value = blob;

        if (!isSupportedFileExtension(base64WithMetaData.extension)) return;

        fileExtension.value = base64WithMetaData.extension;
        fileContentType.value = base64WithMetaData.contentType;
        const rawFileDataResult = await getRawFileData(fileBlob.value);

        if (rawFileDataResult.status === 200) {
            rawFileData.value = rawFileDataResult.object as Uint8Array | string;
            return;
        } else if (rawFileDataResult.status === 422) {
            toast.error('FileCantProcess');
            return;
        } else {
            toast.error('FileCantRead');
            return;
        }
    } catch (error) {
        toast.error('FileCantLoad');
        console.error(error);
    } finally {
        isInitializing.value = false;
    }
});

function getFileBlobUrl(blob: Blob | null): string | undefined {
    if (!blob) return;

    return URL.createObjectURL(blob);
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <div v-if="!isSupportedFileExtension(fileExtension)">
                <p>Deze bestandsextensie wordt nog niet ondersteund, download het bestand om de inhoud hiervan te bekijken.</p>
            </div>

            <div v-if="rawFileData">
                <iframe v-if="fileContentType === 'application/pdf'" :src="`${getFileBlobUrl(fileBlob)}`" style="width: 100%; height: 65vh"></iframe>
                <div v-else-if="fileExtension === 'txt'" class="shadow-sm pa-10" style="white-space: pre-wrap">
                    {{ rawFileData }}
                </div>
                <div v-else-if="fileContentType?.startsWith('image/')" class="pa-2">
                    <img :src="getFileBlobUrl(fileBlob)" alt="Afbeelding kon niet geladen worden..." style="max-height: 60vh; max-width: 80vw" />
                </div>
            </div>
        </div>
    </v-fade-transition>
</template>
